import isNil from "lodash/isNil";
import type { AiChatbotChunkEntity } from "../../../../services/server-side-events-ai-chatbot/entities/ai-chatbot-chunk.entity";
import type { IChatbotMessage } from "../../entities/chatbot-message.entity";
import { RateLimitExceededError } from "../../errors/rate-limit-exceeded.error";
import { StreamClosedWithError } from "../../errors/stream-closed-with.error";
import type { AskToChatbotParams } from "../../lib/mediator/chatbot.mediator.interface";
import type { IChatbotDispatcherService } from "../../lib/services/chatbot-dispatcher/chatbot.dispatcher.service.interface";
import { ChatbotMessageHandlerService } from "../../lib/services/chatbot-message-handler/chatbot-message-handler.service";
import type { IChatbotMessageHandlerService } from "../../lib/services/chatbot-message-handler/chatbot-message-handler.service.interface";
import { ChatbotPromptService } from "../../lib/services/chatbot-prompt/chatbot-prompt.service";
import type { IChatbotPromptService } from "../../lib/services/chatbot-prompt/chatbot-prompt.service.interface";
import type { IStreamMessageService } from "../stream-message/stream-message-service.interface";
import { StreamMessageService } from "../stream-message/stream-message.service";
import { UserMessageAppendStrategy } from "./chatbot-stream-message-handler.enum";
import type { IChatbotMessageStreamHandler } from "./chatbot-stream-message-handler.interface";

export class ChatbotMessageStreamHandler implements IChatbotMessageStreamHandler {
  private _chunkCollection = new Map<number, IChatbotMessage>();

  constructor(
    private readonly _chatbotDispatcherService: IChatbotDispatcherService,
    private readonly _chatbotMessageHandlerService: IChatbotMessageHandlerService = new ChatbotMessageHandlerService(),
    private readonly _claraStreamMessageService: IStreamMessageService = new StreamMessageService(),
    private readonly _chatbotPromptService: IChatbotPromptService = new ChatbotPromptService()
  ) {}

  public async processMessage(
    params: AskToChatbotParams,
    appendStrategy: UserMessageAppendStrategy = UserMessageAppendStrategy.APPEND
  ): Promise<void> {
    this._chunkCollection.clear();

    this._chatbotDispatcherService.setIsSendingAction(true);
    this._chatbotDispatcherService.setShowEmptyState(false);

    const userMessage = this._chatbotMessageHandlerService.generateUserMessage(params.message);

    if (appendStrategy === UserMessageAppendStrategy.APPEND) {
      this._chatbotDispatcherService.appendUserMessage(userMessage);
    }

    await this._chatbotPromptService.storePrompt(userMessage.text);
    this._askToAssistant();
  }

  private _askToAssistant(): void {
    this._claraStreamMessageService.listen(
      this._handleStreamingChunk.bind(this),
      this._handleStreamingError.bind(this)
    );
  }

  private _handleStreamingChunk(this: ChatbotMessageStreamHandler, chunk: AiChatbotChunkEntity): void {
    const sequenceNumber = chunk.sequenceNumber;

    if (chunk.isFirstChunk) {
      this._initializeAiMessageStreamUiFeedback(chunk.payload);
      return;
    }

    const receivedChunk = this._chatbotMessageHandlerService.generateAssistantMessage(chunk.payload);
    receivedChunk.id = chunk.messageId;

    this._chunkCollection.set(sequenceNumber, receivedChunk);
    const chunks = Array.from(this._chunkCollection.entries()).sort(([seqA], [seqB]) => seqA - seqB);
    for (const [_, chunk] of chunks) {
      if (isNil(chunk)) {
        break;
      }
      this._chatbotDispatcherService.appendAiMessageChunk(chunk);
      this._chunkCollection.clear();
    }
  }

  private _initializeAiMessageStreamUiFeedback(message: string): void {
    const aiMessage = this._chatbotMessageHandlerService.generateAssistantMessage(message);

    this._chatbotDispatcherService.appendAiMessage(aiMessage);
    this._chatbotDispatcherService.setShowEmptyState(false);
    this._chatbotDispatcherService.setIsSendingAction(false);
    this._chatbotDispatcherService.setStartStreamingResponse();
  }

  private _handleStreamingError(this: ChatbotMessageStreamHandler, error: Error): void {
    this._chatbotDispatcherService.setEndStreamingResponse();
    if (error instanceof StreamClosedWithError) {
      this._chatbotDispatcherService.setErrorWhileGeneratingResponse(true);
    } else if (error instanceof RateLimitExceededError) {
      this._chatbotDispatcherService.setErrorMessage(error.message);
      this._chatbotDispatcherService.setRateLimitExceeded(true);
      this._chatbotDispatcherService.setShowRetryButton(false);
    }

    this._chunkCollection.clear();
  }
}
